// Internal-access gate for the in-progress admin/gallery migration.
//
// While we rebuild photohawk-admin and photohawk-app functionality inside this
// app, the new screens are only *routed to* internally — i.e. for the internal
// builder. Everyone else keeps the existing behaviour (classic admin), so there
// is no change for real users.
//
// This is a client-side routing/feature gate for build-out, NOT a security
// boundary: photohawk-serverless still authorises every request by token + role.
// See docs/migration/migration-plan.md ("The internal gate").

export const INTERNAL_ADMIN_EMAILS = [
  "andy@photohawk.io",
  "andy@cloudconcur.com",
] as const;

/** True if the given email belongs to the internal migration builder(s). */
export const isInternalAdminEmail = (email?: string | null): boolean => {
  if (!email) return false;
  const normalised = email.trim().toLowerCase();
  return INTERNAL_ADMIN_EMAILS.some((e) => e === normalised);
};

// The internal "/platform" area has NO list here: the staff roles (owner / support) live in
// photohawk-serverless lib staffRoles.js, and the web reads the caller's role from
// GET /organizations → platformRole (see lib/platformRoles.ts and lib/api/platform.ts).
// INTERNAL_ADMIN_EMAILS above is unrelated: it is the v2-admin migration builder gate.

// ---------------------------------------------------------------------------
// v2 workspace-admin rollout gate — MIRRORS photohawk-admin's adminVersion.js
// (V2_SWITCHER_EMAILS + V2_SWITCHER_FOR_ALL_AT), so the "new admin" banner in the
// classic admin and the ability to actually USE the v2 workspace admin open to the
// same people at the same time. UX/routing gate only, not the security boundary.
//
// Before V2_WORKSPACE_FOR_ALL_AT only the pilot users below may enter the v2
// workspace admin; from that instant on, every authenticated workspace member may.

export const V2_WORKSPACE_ADMIN_EMAILS = [
  "andy@photohawk.io",
  "andy@photohawk.com",
  "andy@cloudconcur.com",
  "amy@photohawk.com",
  "rach@photohawk.com",
  "jamie@photohawk.com",
] as const;

// Scheduled general rollout: epoch-ms instant at/after which EVERY signed-in
// workspace user may use the v2 admin. 1785916800000 = 2026-08-05T08:00:00Z
// (Wednesday 08:00 UTC). Keep in sync with photohawk-admin's V2_SWITCHER_FOR_ALL_AT.
// Set to null to disable the scheduled rollout (pilot-only until changed).
export const V2_WORKSPACE_FOR_ALL_AT: number | null = 1785916800000;

/**
 * Whether this user may use the v2 workspace admin right now: everyone once we pass
 * the scheduled rollout instant, otherwise only the named pilot users. Time-based so
 * it flips itself on with no redeploy (mirrors the classic-admin banner gate).
 */
export const canUseV2WorkspaceAdmin = (email?: string | null, nowMs: number = Date.now()): boolean => {
  if (V2_WORKSPACE_FOR_ALL_AT != null && nowMs >= V2_WORKSPACE_FOR_ALL_AT) return true;
  if (!email) return false;
  const normalised = email.trim().toLowerCase();
  return V2_WORKSPACE_ADMIN_EMAILS.some((e) => e === normalised);
};
